/**
 * RegisterAttesterModal — collects a display label for the attester passkey.
 *
 * Simple modal: one text input for the label, Cancel, and Register.
 * Submitting triggers navigator.credentials.create (via useRegisterAttester).
 *
 * Accessibility:
 *   - role="dialog", aria-modal="true", aria-labelledby.
 *   - Focus is placed on the first field via useEffect + ref.
 *   - Escape key closes the modal (window event listener, no jsx-a11y conflict).
 *   - 44px min-height on interactive elements.
 */

import { F } from '@/design/fonts';
import { RADIUS, SPACE, T, TRANSITION } from '@/design/tokens';
import { useModalFocus } from '@/hooks/useModalFocus';
import type { CSSProperties } from 'react';
import { useEffect, useId } from 'react';

export interface RegisterAttesterModalProps {
  isOpen: boolean;
  error: string | null;
  isPending: boolean;
  onConfirm: (label: string) => void;
  onCancel: () => void;
}

export function RegisterAttesterModal({
  isOpen,
  error,
  isPending,
  onConfirm,
  onCancel,
}: RegisterAttesterModalProps) {
  const titleId = useId();
  const { dialogRef, initialRef: inputRef } = useModalFocus<HTMLDivElement, HTMLInputElement>(
    isOpen,
  );

  // Escape key closes the modal (not while pending — cancel is disabled).
  useEffect(() => {
    if (!isOpen) return;
    function onKey(e: KeyboardEvent) {
      if (e.key === 'Escape' && !isPending) onCancel();
    }
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [isOpen, isPending, onCancel]);

  if (!isOpen) return null;

  function handleSubmit(e: React.FormEvent<HTMLFormElement>) {
    e.preventDefault();
    const form = e.currentTarget;
    const data = new FormData(form);
    const label = (data.get('label') as string | null)?.trim() ?? '';
    if (!label) return;
    onConfirm(label);
  }

  const overlayStyle: CSSProperties = {
    position: 'fixed',
    inset: 0,
    background: 'rgba(0,0,0,0.6)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: SPACE.md,
    zIndex: 1000,
  };

  const dialogStyle: CSSProperties = {
    background: T.surface,
    border: `1px solid ${T.border}`,
    borderRadius: RADIUS.md,
    padding: `${SPACE.md}px`,
    maxWidth: 440,
    width: '100%',
    fontFamily: F.body,
  };

  const labelStyle: CSSProperties = {
    display: 'block',
    fontSize: 13,
    fontWeight: 600,
    color: T.text,
    marginBottom: SPACE.sm,
  };

  const inputStyle: CSSProperties = {
    width: '100%',
    boxSizing: 'border-box',
    background: T.card,
    border: `1px solid ${T.border}`,
    borderRadius: RADIUS.md,
    padding: SPACE.sm,
    color: T.text,
    fontSize: 13,
    fontFamily: F.body,
    minHeight: 44,
  };

  const primaryBtnStyle: CSSProperties = {
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    background: T.accent,
    color: T.onAccent,
    border: 'none',
    borderRadius: RADIUS.md,
    padding: `${SPACE.sm}px ${SPACE.md}px`,
    fontSize: 13,
    fontWeight: 700,
    cursor: isPending ? 'not-allowed' : 'pointer',
    minHeight: 44,
    opacity: isPending ? 0.6 : 1,
    transition: `opacity ${TRANSITION.fast}`,
  };

  const cancelBtnStyle: CSSProperties = {
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    background: 'transparent',
    color: T.textMuted,
    border: `1px solid ${T.border}`,
    borderRadius: RADIUS.md,
    padding: `${SPACE.sm}px ${SPACE.md}px`,
    fontSize: 13,
    fontWeight: 600,
    cursor: 'pointer',
    minHeight: 44,
    transition: `opacity ${TRANSITION.fast}`,
  };

  return (
    <div style={overlayStyle}>
      <div
        ref={dialogRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        style={dialogStyle}
      >
        <h2
          id={titleId}
          style={{
            fontSize: 16,
            fontWeight: 700,
            color: T.text,
            margin: 0,
            marginBottom: SPACE.md,
          }}
        >
          Register as attester
        </h2>

        <p style={{ fontSize: 13, color: T.textMuted, lineHeight: 1.6, marginBottom: SPACE.md }}>
          You will be prompted to authenticate with your device passkey (Touch ID / Face ID). A
          label helps identify this credential later.
        </p>

        <form onSubmit={handleSubmit}>
          <label style={labelStyle} htmlFor="register-label">
            Label
          </label>
          <input
            ref={inputRef}
            id="register-label"
            name="label"
            type="text"
            style={inputStyle}
            placeholder="e.g. John Smith, Concours Specialist"
            required
            minLength={2}
            maxLength={80}
            disabled={isPending}
          />

          {error ? (
            <p
              role="alert"
              style={{
                fontSize: 13,
                color: T.red,
                marginTop: SPACE.sm,
                lineHeight: 1.5,
              }}
            >
              {error}
            </p>
          ) : null}

          <div
            style={{
              display: 'flex',
              gap: SPACE.sm,
              marginTop: SPACE.md,
              justifyContent: 'flex-end',
            }}
          >
            <button type="button" style={cancelBtnStyle} onClick={onCancel} disabled={isPending}>
              Cancel
            </button>
            <button type="submit" style={primaryBtnStyle} disabled={isPending}>
              {isPending ? 'Waiting for passkey…' : 'Register'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
